import React, { useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import DefaultLayout from "../../layout/DefaultLayout";
import BreadcrumbTwo from "../../components/Breadcrumbs/BreadcrumbTwo";
import TextAreaField from "../../components/Forms/Input/TextAreaField";
import { useQzTraySigner } from "../../hooks/qztray/useQzTraySigner";
import VerificationResultBox from "../../components/qztray/signature/VerificationResultBox";
import VerificationErrorBox from "../../components/qztray/signature/VerificationErrorBox";

const QzTrayVerifySignature: React.FC = () => {
  const {
    payload,
    setPayload,
    handleVerify,
    loading,
    error,
    setCertificateType,
  } = useQzTraySigner();

  const [signatureInput, setSignatureInput] = useState("");
  const [verificationResult, setVerificationResult] = useState<"valid" | "invalid" | null>(null);

  // ✅ Forzar uso de CERTIFICATE siempre
  React.useEffect(() => {
    setCertificateType("CERTIFICATE");
  }, []);

  const handleVerifyClick = async () => {
    if (!payload || !signatureInput) {
      alert("Por favor, ingresa el payload y la firma antes de verificar.");
      return;
    }

    try {
      const result = await handleVerify(payload, signatureInput, "CERTIFICATE");

      setVerificationResult(result?.valid ? "valid" : "invalid");

    } catch (err) {
      console.error("❌ Error al verificar:", err);
      setVerificationResult("invalid");
    }
  };

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <div className="w-full bg-white dark:bg-boxdark p-10 rounded-none shadow-md">
        <h2 className="mb-4 text-2xl font-semibold text-gray-800 dark:text-white">
          🔍 Verificación de Firma Digital
        </h2>

        <p className="mb-8 text-gray-600 dark:text-gray-400 max-w-3xl">
          Verifica si una firma digital es válida para un payload usando el certificado activo.
        </p>

        <div className="space-y-6">

          {/* JSON */}
          <TextAreaField
            label="Payload"
            name="payload"
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            rows={6}
            placeholder='Ejemplo: {"id":123,"total":45.90}'
            iconKey="edit"
          />

          {/* Firma */}
          <TextAreaField
            label="Firma digital (Base64)"
            name="signature"
            value={signatureInput}
            onChange={(e) => setSignatureInput(e.target.value)}
            rows={5}
            placeholder="Pega aquí la firma..."
            iconKey="key"
          />

          {/* Botón */}
          <div className="flex justify-end">
            <motion.button
              whileTap={{ scale: 0.97 }}
              disabled={loading}
              onClick={handleVerifyClick}
              className={`flex items-center gap-2 rounded-md bg-primary px-6 py-2.5 text-white font-medium shadow hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-400`}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Verificar Firma
            </motion.button>
          </div>
        </div>

        {/* ✅ Resultado */}
        <VerificationResultBox status={verificationResult} />

        {/* ⚠️ Error */}
        {error && <VerificationErrorBox message={error} />}
      </div>
    </DefaultLayout>
  );
};

export default QzTrayVerifySignature;
